import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { KbChatClient, KbApiError } from '../api/client';
import { KbChatMessage } from '../api/types';
import { SavedConversation, SessionStore } from './sessionStore';
import { defaultStorageKind, type AskJuminoConfig } from '../config';

export type ChatView = 'choice' | 'chat';

export interface UseChatResult {
  view: ChatView;
  messages: KbChatMessage[];
  isLoading: boolean;
  errorMessage: string | null;
  sessionId: string | null;
  /** Epoch ms until which sending is rate-limited, or null. Drives the Composer's
   * countdown so a limited user is told how long to wait. */
  cooldownUntil: number | null;
  dismissError: () => void;
  continuePrevious: () => void;
  startNew: () => void;
  /** Resolves true when the question was answered. False means the caller should
   * keep the user's text in the composer so it isn't lost. */
  sendMessage: (question: string) => Promise<boolean>;
  /** Idempotent — safe to call from both a Close button and unmount. */
  teardown: () => void;
}

export interface UseChatCallbacks {
  onSessionCreated?: (id: string) => void;
  /** Mirrors the widget's aj:error event — see element.ts. */
  onError?: (info: { phase: 'session' | 'chat'; status?: number; message: string }) => void;
  onAnswer?: (info: { sessionId: string | null; sourceCount: number }) => void;
}

export function useChat(config: AskJuminoConfig, callbacks: UseChatCallbacks = {}): UseChatResult {
  const client = useRef(
    new KbChatClient({ baseUrl: config.baseUrl, getHeaders: config.getHeaders }),
  );
  const store = useRef(
    new SessionStore(
      config.storageKey ?? 'ask-jumino-conversation',
      // Resolved here too, not just in the custom element, so App used directly
      // (tests, or a host rendering Preact itself) follows the same rule.
      config.storage ?? defaultStorageKind(config),
    ),
  );

  const [view, setView] = useState<ChatView>('chat');
  const [messages, setMessages] = useState<KbChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  const pendingPrevious = useRef<SavedConversation | null>(null);
  // Mirrors sessionId/messages into refs so teardown() (called from unmount,
  // where the latest state closure may be stale) always sees current values.
  const sessionIdRef = useRef<string | null>(null);
  const messagesRef = useRef<KbChatMessage[]>([]);
  // Set once teardown() has run, so the pagehide listener below and a second
  // teardown() call can both no-op instead of re-saving a finished conversation.
  const torn = useRef(false);
  sessionIdRef.current = sessionId;
  messagesRef.current = messages;

  const startNewSession = useCallback(() => {
    setView('chat');
    setMessages([]);
    setSessionId(null);

    setIsLoading(true);
    setErrorMessage(null);
    client.current
      .createSession(config.mode)
      .then(res => {
        setSessionId(res.session_id);
        setIsLoading(false);
        callbacks.onSessionCreated?.(res.session_id);
      })
      .catch(err => {
        setIsLoading(false);
        // 401 means the portal token is missing or expired. "Try again" is bad
        // advice there — retrying with the same dead token fails identically.
        const expired = err instanceof KbApiError && err.status === 401;
        setErrorMessage(
          expired
            ? 'Your session has expired. Please refresh the page to sign in again.'
            : 'Failed to start a chat session. Please try again.',
        );
        // eslint-disable-next-line no-console
        console.error('Ask Jumino session error:', err);
        callbacks.onError?.({
          phase: 'session',
          status: err instanceof KbApiError ? err.status : undefined,
          message: err instanceof Error ? err.message : String(err),
        });
      });
  }, [config.baseUrl, config.mode]);

  // resumePrompt false (default for technical-sales) skips the *prompt*, not
  // the resume — a saved conversation is still reused, just silently.
  useEffect(() => {
    const resumePrompt = config.resumePrompt ?? config.mode === 'support';
    const prev = store.current.getPrevious();
    if (prev && prev.messages.length > 0) {
      if (resumePrompt) {
        pendingPrevious.current = prev;
        setView('choice');
      } else {
        setSessionId(prev.sessionId);
        setMessages(prev.messages);
        setView('chat');
      }
    } else {
      startNewSession();
    }
    return () => teardown(); // unmount without close() still needs teardown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the page never runs teardown() — browsers don't unmount custom
  // elements on unload — so mirror the conversation on pagehide too. Without
  // this, a visitor moving between pages of the host site with the panel open
  // loses the thread on every navigation, which is exactly the case
  // storage: 'session' is meant to keep.
  useEffect(() => {
    const onPageHide = () => {
      const id = sessionIdRef.current;
      // Nothing said yet: no conversation worth keeping. Skipping the DELETE
      // teardown() would do here is deliberate — an unload-time fetch is
      // unreliable, and the backend's TTL sweep collects the empty session.
      if (torn.current || !id || messagesRef.current.length === 0) return;
      store.current.save(id, messagesRef.current);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  const continuePrevious = useCallback(() => {
    if (pendingPrevious.current) {
      setSessionId(pendingPrevious.current.sessionId);
      setMessages(pendingPrevious.current.messages);
      pendingPrevious.current = null;
    }
    setView('chat');
  }, []);

  const startNew = useCallback(() => {
    if (pendingPrevious.current) {
      // Best-effort: free the old backend session we're abandoning.
      client.current.deleteSession(pendingPrevious.current.sessionId).catch(err => {
        // eslint-disable-next-line no-console
        console.error('Ask Jumino session cleanup error:', err);
      });
      pendingPrevious.current = null;
    }
    store.current.clear();
    startNewSession();
  }, [startNewSession]);

  const sendMessage = useCallback(
    async (question: string): Promise<boolean> => {
      const trimmed = question.trim();
      if (!trimmed || !sessionIdRef.current || isLoading) {
        return false;
      }
      const activeSessionId = sessionIdRef.current;
      setMessages(prev => [...prev, { role: 'user', content: trimmed, timestamp: new Date() }]);
      setIsLoading(true);
      setErrorMessage(null);
      // Clear any previous cooldown: sending is blocked while one is active, so
      // reaching here means it elapsed. Leaving it set would make later,
      // unrelated errors render as a rate-limit notice.
      setCooldownUntil(null);

      try {
        const res = await client.current.sendMessage({
          question: trimmed,
          session_id: activeSessionId,
        });
        setIsLoading(false);
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: res.answer, timestamp: new Date(), sources: res.sources },
        ]);
        callbacks.onAnswer?.({ sessionId: res.session_id, sourceCount: res.sources.length });
        return true;
      } catch (err) {
        setIsLoading(false);
        const rateLimited = err instanceof KbApiError && err.status === 429;
        // A rate limit is a "wait", not a fault: show what the server actually
        // said (it names the wait) rather than a generic failure.
        setErrorMessage(
          rateLimited && err.message ? err.message : 'Failed to send message. Please try again.',
        );
        if (rateLimited && err.retryAfter) {
          setCooldownUntil(Date.now() + err.retryAfter * 1000);
        }
        // Drop the user's bubble — Composer keeps the text in the input on a
        // false result, so leaving the bubble too would show the question twice.
        setMessages(prev => prev.slice(0, -1));
        // eslint-disable-next-line no-console
        console.error('Ask Jumino chat error:', err instanceof KbApiError ? err.message : err);
        callbacks.onError?.({
          phase: 'chat',
          status: err instanceof KbApiError ? err.status : undefined,
          message: err instanceof Error ? err.message : String(err),
        });
        return false;
      }
    },
    [isLoading],
  );

  const teardown = useCallback(() => {
    if (torn.current) return; // idempotent — close() and unmount both call this
    torn.current = true;
    const id = sessionIdRef.current;
    if (!id) return;
    if (messagesRef.current.length > 0) {
      store.current.save(id, messagesRef.current);
    } else {
      client.current.deleteSession(id).catch(err => {
        // eslint-disable-next-line no-console
        console.error('Ask Jumino session cleanup error:', err);
      });
    }
  }, []);

  return {
    view,
    messages,
    isLoading,
    errorMessage,
    sessionId,
    cooldownUntil,
    dismissError: () => {
      setErrorMessage(null);
      setCooldownUntil(null);
    },
    continuePrevious,
    startNew,
    sendMessage,
    teardown,
  };
}
